export const MONTHS = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
] as const;

export type CalendarMonth = (typeof MONTHS)[number];

export type MonthString = CalendarMonth | "NOT_SPECIFIED";

export interface ProfileDate {
  month?: MonthString | null;
  year?: number | null;
}

export interface ProfileDateRange {
  isCurrent?: boolean | null;
  fromDate?: ProfileDate | null;
  toDate?: ProfileDate | null;
}

interface HasDateRange {
  dateRange?: ProfileDateRange | null;
}

export function getMonthIndex(month: MonthString): number {
  if (month === "NOT_SPECIFIED") return -1;
  return MONTHS.indexOf(month);
}

/**
 * Compares two dates, returning a negative number if a > b (descending order).
 */
export const compareDatesDescending = (
  a: ProfileDate | null | undefined,
  b: ProfileDate | null | undefined,
): number => {
  // Items without date go after items with date
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  // Compare years first
  const aYear = a.year ?? 0;
  const bYear = b.year ?? 0;
  if (aYear !== bYear) return bYear - aYear;

  // Compare months
  const aMonth = a.month ? getMonthIndex(a.month) : -1;
  const bMonth = b.month ? getMonthIndex(b.month) : -1;
  return bMonth - aMonth;
};

interface HasDate {
  date?: ProfileDate | null;
}

/**
 * Comparator for Array.sort that orders objects by their single `date` field,
 * newest first (items without a date sort to the end). The single-date peer of
 * {@link sortByDateRange}.
 */
export const sortByDate = <T extends HasDate>(a: T, b: T): number =>
  compareDatesDescending(a.date, b.date);

/**
 * Comparator function for Array.sort that sorts objects by their dateRange.
 * Sort order (descending by end date):
 * 1. Items with isCurrent=true (current positions first, sorted by fromDate descending)
 * 2. Items with dateRange, sorted by endDate (toDate) descending
 * 3. Items without dateRange (at the end)
 */
export const sortByDateRange = <T extends HasDateRange>(a: T, b: T): number => {
  const aDateRange = a.dateRange;
  const bDateRange = b.dateRange;

  // Items without dateRange go to the end
  if (!aDateRange && !bDateRange) return 0;
  if (!aDateRange) return 1;
  if (!bDateRange) return -1;

  // Current items go to the top
  if (aDateRange.isCurrent && !bDateRange.isCurrent) return -1;
  if (!aDateRange.isCurrent && bDateRange.isCurrent) return 1;

  // Both are current: sort by fromDate descending
  if (aDateRange.isCurrent && bDateRange.isCurrent) {
    return compareDatesDescending(aDateRange.fromDate, bDateRange.fromDate);
  }

  // Sort by toDate descending, using fromDate as tiebreaker
  const toDateComparison = compareDatesDescending(aDateRange.toDate, bDateRange.toDate);
  if (toDateComparison !== 0) return toDateComparison;

  return compareDatesDescending(aDateRange.fromDate, bDateRange.fromDate);
};

const NUMBER_TO_MONTH_ENUM = Object.fromEntries(
  MONTHS.map((month, index) => [String(index + 1), month]),
) as Record<string, CalendarMonth>;

const MONTH_ENUM_TO_NUMBER = Object.fromEntries(
  MONTHS.map((month, index) => [month, String(index + 1)]),
) as Record<string, string>;

/**
 * Convert a month number string to the month enum.
 *
 * @param monthNumber - Month as number string "1"-"12", or empty/undefined
 * @returns The month enum value, or undefined if input is empty
 *
 * @example
 * numberToMonthEnum("1")  // "JANUARY"
 * numberToMonthEnum("12") // "DECEMBER"
 * numberToMonthEnum("")   // undefined
 */
export function numberToMonthEnum(monthNumber?: string): CalendarMonth | undefined {
  if (!monthNumber) return undefined;
  return NUMBER_TO_MONTH_ENUM[monthNumber];
}

/**
 * Convert a month enum to a number string for form selects.
 *
 * @param monthEnum - Month enum like "JANUARY", or null/undefined
 * @returns Month as number string "1"-"12", or empty string if input is empty
 *
 * @example
 * monthEnumToNumber("JANUARY")  // "1"
 * monthEnumToNumber("DECEMBER") // "12"
 * monthEnumToNumber(null)       // ""
 */
export function monthEnumToNumber(monthEnum?: string | null): string {
  if (!monthEnum) return "";
  return MONTH_ENUM_TO_NUMBER[monthEnum] ?? "";
}

/**
 * Check whether an end month/year precedes a start month/year.
 *
 * Designed for form values where month and year are separate string fields
 * (month: "1"–"12", year: "2020"). Returns `false` (not sequential) when the
 * end date is strictly before the start date. Partial or empty dates are
 * considered valid (returns `true`).
 *
 * @example
 * isDateRangeSequential('5', '2023', '6', '2020') // false
 * isDateRangeSequential('1', '2020', '6', '2023') // true
 * isDateRangeSequential('', '', '', '')            // true (no dates)
 */
export function isDateRangeSequential(
  startMonth: string,
  startYear: string,
  endMonth: string,
  endYear: string,
): boolean {
  if (!endYear || !startYear) return true;

  const endYearNum = parseInt(endYear, 10);
  const startYearNum = parseInt(startYear, 10);
  if (isNaN(endYearNum) || isNaN(startYearNum)) return true;

  if (endYearNum < startYearNum) return false;

  if (endYearNum === startYearNum && endMonth && startMonth) {
    return parseInt(endMonth, 10) >= parseInt(startMonth, 10);
  }

  return true;
}
